import { useCallback, useContext, useEffect } from 'react';
import { themeJustSuper, themeLight, themeVisualBreak } from '@lib/nafTheme';
import { useLocation } from 'react-router';
import { ThemeContextAction } from '@lib/ThemeContext';
import { ThemeVariants } from '../../../types/themes';
import { ArticleType } from '../../../types/articleType';
import type { RootState } from '../redux';
import useSelector from '../redux/typedHooks';

// The theme a page asks for: its document's own, else its category's, else its (parent's) category's.
// Selected as one string because ThemedApp re-renders on every change to what useTheme selects, and
// the documents and site-structure slices change several times per navigation while the theme almost
// never does.
const selectThemeVariant = (state: RootState, slug: string): ThemeVariants | null => {
  const documentData = state.documents.mappedData[slug]?.data as ArticleType | undefined;
  const { mappedCategories } = state.sitestructure;
  const siteStructureData = slug ? mappedCategories[slug]?.data : undefined;

  if (documentData?.theme) return documentData.theme;
  if (!documentData && siteStructureData?.theme) return siteStructureData.theme;
  if (!siteStructureData && documentData?.category) {
    return mappedCategories[documentData.category.slug]?.data?.theme || null;
  }
  // Subarticle - the parent's category
  if (!siteStructureData && documentData && !documentData.category && documentData.parent?.category?.slug) {
    return mappedCategories[documentData.parent.category.slug]?.data?.theme || null;
  }
  return null;
};

export const useTheme = (initialTheme?: ThemeVariants | null) => {
  const { pathname } = useLocation();
  const setTheme = useContext(ThemeContextAction);
  const slug = pathname.split('/').slice(-1)[0];
  const themeVariant = useSelector((state) => selectThemeVariant(state, slug));

  const handleSwitchTheme = useCallback(
    (theme?: ThemeVariants | null) => {
      if (theme === ThemeVariants.Standard) {
        setTheme(themeLight);
      } else if (theme === ThemeVariants.VisualBreak) {
        setTheme(themeVisualBreak);
      } else if (theme === ThemeVariants.JustSuper) {
        setTheme(themeJustSuper);
      } else {
        setTheme(themeLight);
      }
    },
    [setTheme],
  );

  // Set initial theme from server data
  useEffect(() => {
    if (handleSwitchTheme && initialTheme) {
      handleSwitchTheme(initialTheme);
    }
  }, [handleSwitchTheme, initialTheme]);

  // `pathname` re-applies the page's theme on every navigation, not only when the variant changes:
  // a topic page's ColorSection sets the context theme as a section scrolls into view and never resets
  // it, so the next page would otherwise keep that section's colours. Setting the same theme is a no-op.
  useEffect(() => {
    handleSwitchTheme(themeVariant);
  }, [themeVariant, pathname, handleSwitchTheme]);
};
